import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { ArrowLeft, Database, Search } from "lucide-react";
import { toast } from "sonner";

export default function AdminProduction() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!user?.is_admin) return;
    setLoading(true);
    api.get("/admin/production-stats")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load production stats"))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/" replace />;

  const rows = (data?.per_therapist || []).filter((r) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (r.name || "").toLowerCase().includes(s) || (r.email || "").toLowerCase().includes(s);
  });

  return (
    <Layout>
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-subnav">
        <Link to="/admin/usage" data-testid="admin-subnav-usage" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">AI Usage</Link>
        <Link to="/admin/production" data-testid="admin-subnav-production" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-ink-900 text-white inline-flex items-center gap-2">Production</Link>
        <Link to="/admin/billing" data-testid="admin-subnav-billing" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">Billing & Verification</Link>
        <Link to="/admin/feedback" data-testid="admin-subnav-feedback" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">Feedback</Link>
      </div>

      <div className="mb-8">
        <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-terracotta-500/10 text-terracotta-600 text-xs uppercase tracking-[0.14em]">
          <Database className="w-3.5 h-3.5" /> Admin only
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900 mb-1">Production usage</h1>
        <p className="text-ink-700">Live per-therapist counts, scoped by ownership. Nothing hard-coded.</p>
      </div>

      {loading && <div className="text-ink-500">Loading…</div>}
      {!loading && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="production-totals">
            <Stat label="Therapist accounts" value={data.totals.therapist_accounts} testid="total-therapists" />
            <Stat label="Total clients" value={data.totals.total_clients} testid="total-clients" />
            <Stat label="Total sessions" value={data.totals.total_sessions} testid="total-sessions" />
            <Stat label="Total photos" value={data.totals.total_photos} testid="total-photos" />
          </div>

          {data.reconciliation && (
            <div
              data-testid="production-reconciliation"
              className={`text-xs mb-6 px-3 py-2 rounded-xl border ${
                data.reconciliation.clients_match && data.reconciliation.sessions_match && data.reconciliation.photos_match
                  ? "border-sage-500/40 bg-sage-500/5 text-sage-700"
                  : "border-terracotta-500/40 bg-terracotta-500/5 text-terracotta-700"
              }`}
            >
              {data.reconciliation.clients_match && data.reconciliation.sessions_match && data.reconciliation.photos_match
                ? "✓ Overall totals reconcile exactly with the sum of per-therapist counts."
                : "⚠ Totals do not reconcile — investigate."}
              {(data.reconciliation.orphan_clients || data.reconciliation.orphan_sessions || data.reconciliation.orphan_photos) ? (
                <span> Orphaned (no owner account): {data.reconciliation.orphan_clients} clients · {data.reconciliation.orphan_sessions} sessions · {data.reconciliation.orphan_photos} photos.</span>
              ) : null}
            </div>
          )}

          <div className="relative mb-4 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              data-testid="production-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by therapist name or email"
              className="w-full min-h-[44px] pl-9 pr-4 rounded-xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm"
            />
          </div>

          <div className="bg-white border border-paper-200 rounded-3xl overflow-hidden" data-testid="production-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-paper-50 border-b border-paper-200 text-left">
                  <tr>
                    <Th>Therapist</Th>
                    <Th align="right">Clients</Th>
                    <Th align="right">Active</Th>
                    <Th align="right">Archived</Th>
                    <Th align="right">Sessions</Th>
                    <Th align="right">Completed</Th>
                    <Th align="right">Draft</Th>
                    <Th align="right">Photos</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-ink-500">No therapists match.</td></tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.therapist_id} className="border-b border-paper-200 last:border-0 hover:bg-paper-50/60" data-testid={`production-row-${r.therapist_id}`}>
                        <Td>
                          <div className="font-medium text-ink-900">{r.name}{r.is_admin ? " ·admin" : ""}</div>
                          <div className="text-xs text-ink-500">{r.email}</div>
                        </Td>
                        <Td align="right" mono>{r.clients_total}</Td>
                        <Td align="right" mono className="text-ink-500">{r.clients_active}</Td>
                        <Td align="right" mono className="text-ink-500">{r.clients_archived}</Td>
                        <Td align="right" mono>{r.sessions_total}</Td>
                        <Td align="right" mono className="text-ink-500">{r.sessions_completed}</Td>
                        <Td align="right" mono className="text-ink-500">{r.sessions_draft}</Td>
                        <Td align="right" mono>{r.photos_total}</Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-ink-500 mt-4">
            Showing {rows.length} of {data.per_therapist.length} therapist accounts, most active first. Photos are counted from stored photo records (excluding soft-deleted), owned directly by the therapist.
          </p>
        </>
      )}
    </Layout>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-paper-200 rounded-2xl p-5" data-testid={testid}>
      <div className="label-overline mb-2">{label}</div>
      <div className="font-heading text-3xl text-ink-900">{Number(value).toLocaleString()}</div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 label-overline text-${align}`}>{children}</th>;
}

function Td({ children, align = "left", mono = false, className = "" }) {
  const cls = `${mono ? "font-mono tabular-nums" : ""} ${className}`.trim();
  return <td className={`px-4 py-3 text-${align} ${cls}`}>{children}</td>;
}
